import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  Table,
  Badge,
  FormGroup,
} from "reactstrap";
import { injectIntl } from "react-intl";
import axios from "axios";
import { NotificationManager } from "../../../../components/common/react-notifications";
import { END_POINT } from "../../../../constants/defaultValues";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";

const InventoryAuditForm = ({
  intl,
  isOpen,
  toggle,
  onSuccess,
  stocks = [],
  editingAudit = null,
}) => {
  const [auditDate, setAuditDate] = useState(moment());
  const [notes, setNotes] = useState("");
  const [selectedStocks, setSelectedStocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Tạo phiên, 2: Chọn nguyên liệu, 3: Nhập số thực tế
  const [currentAudit, setCurrentAudit] = useState(null);
  const [auditItems, setAuditItems] = useState([]);

  // Nếu đang chỉnh sửa audit có sẵn
  useEffect(() => {
    if (editingAudit && isOpen) {
      setCurrentAudit(editingAudit);
      setAuditDate(moment(editingAudit.audit_date));
      setNotes(editingAudit.notes || "");
      setAuditItems(editingAudit.items || []);

      // Xác định step dựa trên trạng thái
      if (editingAudit.status === "draft") {
        // Nháp - chưa có items, đi tới bước chọn nguyên liệu
        setStep(2);
      } else if (editingAudit.status === "in_progress") {
        // Đang kiểm - đã có items, đi tới bước nhập số thực tế
        setStep(3);
      }
    }
  }, [editingAudit, isOpen]);

  const resetForm = () => {
    setAuditDate(moment());
    setNotes("");
    setSelectedStocks([]);
    setStep(1);
    setCurrentAudit(null);
    setAuditItems([]);
  };

  const handleClose = () => {
    // Nếu đã tạo phiên kiểm kho (có thay đổi), refresh danh sách
    if (currentAudit) {
      onSuccess && onSuccess();
    }
    resetForm();
    toggle();
  };

  // Step 1: Tạo phiên kiểm kho
  const handleCreateAudit = async () => {
    setLoading(true);
    try {
      const tokenStr = localStorage.getItem("access_token");
      const response = await axios.post(
        `${END_POINT}/api/inventory-audit`,
        {
          audit_date: moment(auditDate).format("YYYY-MM-DD"),
          notes,
        },
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
        }
      );

      if (response.data.status === 200) {
        setCurrentAudit(response.data.data);
        setStep(2);
        NotificationManager.success(
          "Tạo phiên kiểm kho thành công",
          "Thành công"
        );
      } else {
        NotificationManager.error(response.data.responseText, "Lỗi");
      }
    } catch (error) {
      NotificationManager.error("Có lỗi xảy ra", "Lỗi");
    }
    setLoading(false);
  };

  // Step 2: Thêm nguyên liệu vào phiên kiểm
  const handleAddItems = async () => {
    if (selectedStocks.length === 0) {
      NotificationManager.warning("Vui lòng chọn nguyên liệu", "Cảnh báo");
      return;
    }

    setLoading(true);
    try {
      const tokenStr = localStorage.getItem("access_token");
      const items = selectedStocks.map((stockId) => ({ stock_id: stockId }));
      const response = await axios.post(
        `${END_POINT}/api/inventory-audit/${currentAudit.id}/items`,
        {
          items,
        },
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
        }
      );

      if (response.data.status === 200) {
        // Lấy chi tiết phiên kiểm kho
        const detailResponse = await axios.get(
          `${END_POINT}/api/inventory-audit/${currentAudit.id}`,
          {
            headers: { Authorization: `Bearer ${tokenStr}` },
          }
        );
        setAuditItems(detailResponse.data.data.items || []);
        setStep(3);
        NotificationManager.success(
          "Thêm nguyên liệu thành công",
          "Thành công"
        );
      } else {
        NotificationManager.error(response.data.responseText, "Lỗi");
      }
    } catch (error) {
      NotificationManager.error("Có lỗi xảy ra", "Lỗi");
    }
    setLoading(false);
  };

  // Step 3: Cập nhật số lượng thực tế
  const handleUpdateActualQuantity = async (itemId, actualQuantity, reason) => {
    try {
      const tokenStr = localStorage.getItem("access_token");
      const response = await axios.put(
        `${END_POINT}/api/inventory-audit/${currentAudit.id}/items/${itemId}`,
        {
          actual_quantity: actualQuantity,
          reason,
        },
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
        }
      );

      if (response.data.status === 200) {
        // Cập nhật local state
        setAuditItems((prevItems) =>
          prevItems.map((item) =>
            item.id === itemId
              ? {
                  ...item,
                  actual_quantity: actualQuantity,
                  discrepancy: actualQuantity - item.system_quantity,
                  reason,
                }
              : item
          )
        );
      }
    } catch (error) {
      NotificationManager.error("Có lỗi xảy ra khi cập nhật", "Lỗi");
    }
  };

  // Hoàn thành kiểm kho
  const handleComplete = async () => {
    // Kiểm tra đã nhập đủ số thực tế chưa
    const incompleteItems = auditItems.filter(
      (item) => item.actual_quantity === null
    );
    if (incompleteItems.length > 0) {
      NotificationManager.warning(
        "Vui lòng nhập số thực tế cho tất cả nguyên liệu",
        "Cảnh báo"
      );
      return;
    }

    setLoading(true);
    try {
      const tokenStr = localStorage.getItem("access_token");
      const response = await axios.post(
        `${END_POINT}/api/inventory-audit/${currentAudit.id}/complete`,
        {},
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
        }
      );

      if (response.data.status === 200) {
        NotificationManager.success(
          "Hoàn thành kiểm kho thành công!",
          "Thành công"
        );
        onSuccess && onSuccess();
        handleClose();
      } else {
        NotificationManager.error(response.data.responseText, "Lỗi");
      }
    } catch (error) {
      NotificationManager.error("Có lỗi xảy ra", "Lỗi");
    }
    setLoading(false);
  };

  const toggleStock = (stockId) => {
    setSelectedStocks((prev) =>
      prev.includes(stockId)
        ? prev.filter((id) => id !== stockId)
        : [...prev, stockId]
    );
  };

  const selectAllStocks = () => {
    setSelectedStocks(stocks.map((s) => s.id));
  };

  const deselectAllStocks = () => {
    setSelectedStocks([]);
  };

  const renderStep1 = () => (
    <>
      <FormGroup>
        <Label>Ngày kiểm kho</Label>
        <DatePicker
          selected={auditDate ? moment(auditDate) : null}
          onChange={(date) => setAuditDate(date)}
          dateFormat="DD-MM-YYYY"
          className="form-control"
        />
      </FormGroup>
      <FormGroup>
        <Label>Ghi chú</Label>
        <Input
          type="textarea"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Kiểm kho đầu năm, kiểm kho hàng tháng..."
        />
      </FormGroup>
    </>
  );

  const renderStep2 = () => (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span>
          Chọn nguyên liệu cần kiểm ({selectedStocks.length}/{stocks.length})
        </span>
        <div>
          <Button color="link" size="sm" onClick={selectAllStocks}>
            Chọn tất cả
          </Button>
          <Button
            color="link"
            size="sm"
            onClick={deselectAllStocks}
            className="text-danger"
          >
            Bỏ chọn tất cả
          </Button>
        </div>
      </div>
      <div
        style={{ maxHeight: "400px", overflowY: "auto", overflowX: "hidden" }}
      >
        <Table hover size="sm" style={{ tableLayout: "fixed" }}>
          <thead>
            <tr>
              <th style={{ width: "40px" }}></th>
              <th>Tên nguyên liệu</th>
              <th className="text-right" style={{ width: "100px" }}>
                Số lượng
              </th>
              <th style={{ width: "80px" }}>Đơn vị</th>
            </tr>
          </thead>
          <tbody>
            {stocks.map((stock) => (
              <tr
                key={stock.id}
                onClick={() => toggleStock(stock.id)}
                style={{ cursor: "pointer" }}
              >
                <td style={{ verticalAlign: "middle" }}>
                  <Input
                    type="checkbox"
                    checked={selectedStocks.includes(stock.id)}
                    onChange={() => {}}
                    style={{ margin: 0, position: "relative" }}
                  />
                </td>
                <td
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {stock.name}
                </td>
                <td className="text-right">{stock.count}</td>
                <td>{stock.unit}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </>
  );

  const renderStep3 = () => (
    <>
      <div className="mb-3">
        <Badge color="info">Mã: {currentAudit?.audit_code}</Badge>
      </div>
      <div
        style={{ maxHeight: "400px", overflowY: "auto", overflowX: "hidden" }}
      >
        <Table size="sm" style={{ tableLayout: "fixed" }}>
          <thead>
            <tr>
              <th style={{ width: "30%" }}>Nguyên liệu</th>
              <th className="text-center" style={{ width: "12%" }}>
                Hệ thống
              </th>
              <th className="text-center" style={{ width: "15%" }}>
                Thực tế
              </th>
              <th className="text-center" style={{ width: "13%" }}>
                Chênh lệch
              </th>
              <th style={{ width: "30%" }}>Lý do chênh lệch</th>
            </tr>
          </thead>
          <tbody>
            {auditItems.map((item) => (
              <tr key={item.id}>
                <td
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    verticalAlign: "middle",
                  }}
                >
                  {item.stock?.name}
                </td>
                <td className="text-center" style={{ verticalAlign: "middle" }}>
                  {item.system_quantity}
                </td>
                <td style={{ verticalAlign: "middle" }}>
                  <Input
                    type="number"
                    bsSize="sm"
                    value={
                      item.actual_quantity === null ? "" : item.actual_quantity
                    }
                    onChange={(e) => {
                      const value =
                        e.target.value === ""
                          ? null
                          : parseFloat(e.target.value);
                      handleUpdateActualQuantity(item.id, value, item.reason);
                    }}
                    placeholder="0"
                    style={{ textAlign: "center" }}
                  />
                </td>
                <td
                  className={`text-center ${
                    item.discrepancy > 0
                      ? "text-success"
                      : item.discrepancy < 0
                      ? "text-danger"
                      : ""
                  }`}
                  style={{ verticalAlign: "middle", fontWeight: "500" }}
                >
                  {item.discrepancy > 0 ? "+" : ""}
                  {item.discrepancy}
                </td>
                <td style={{ verticalAlign: "middle" }}>
                  <Input
                    type="text"
                    value={item.reason || ""}
                    onChange={(e) =>
                      handleUpdateActualQuantity(
                        item.id,
                        item.actual_quantity,
                        e.target.value
                      )
                    }
                    placeholder="Nhập lý do..."
                    style={{ fontSize: "13px" }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </>
  );

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>
        Kiểm Kho -{" "}
        {step === 1
          ? "Tạo phiên"
          : step === 2
          ? "Chọn nguyên liệu"
          : "Nhập số thực tế"}
      </ModalHeader>
      <ModalBody>
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
      </ModalBody>
      <ModalFooter>
        {step > 1 && (
          <Button
            color="secondary"
            onClick={() => setStep(step - 1)}
            disabled={loading}
          >
            Quay lại
          </Button>
        )}
        {step === 1 && (
          <Button
            color="primary"
            onClick={handleCreateAudit}
            disabled={loading}
          >
            {loading ? "Đang xử lý..." : "Tiếp theo"}
          </Button>
        )}
        {step === 2 && (
          <Button color="primary" onClick={handleAddItems} disabled={loading}>
            {loading ? "Đang xử lý..." : "Tiếp theo"}
          </Button>
        )}
        {step === 3 && (
          <Button color="success" onClick={handleComplete} disabled={loading}>
            {loading ? "Đang xử lý..." : "Hoàn thành kiểm kho"}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default injectIntl(InventoryAuditForm);
